import { CircleNotchIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiErrorDetail, apiErrorStatus, useRequestPasswordReset } from "@/lib/api";
import { PROJECT_NAME } from "@/lib/brand-assets";
import { logger } from "@/lib/logger";
import { retryAfterLabel } from "@/lib/retry-after";

// Constant-length JSX expression keeps prettier output stable for any rendered name.

/**
 * Asks for a password-reset email. The backend answers the same way whether or
 * not the account exists, so the page does too.
 */
export function ForgotPasswordPage() {
  const requestMutation = useRequestPasswordReset();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    logger.debug("[auth] requesting a password reset for", email);
    requestMutation.mutate(
      { email },
      {
        onSuccess: () => setSent(true),
        onError: (err) => {
          logger.debug("[auth] reset request failed:", apiErrorStatus(err), apiErrorDetail(err));
          setError(forgotErrorMessage(err));
        },
      },
    );
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card variant="box" className="w-full max-w-md">
        <CardHeader className="space-y-6 text-left">
          <p className="text-muted t-eyebrow">Board of Innovation</p>
          <BrandLogo name={PROJECT_NAME} className="h-7 text-2xl" />
          <div className="space-y-2">
            <CardTitle className="t-title text-foreground">Reset your password</CardTitle>
            <p className="text-muted text-sm leading-6">
              Enter the email you sign in with, and we&apos;ll send you a link to choose a new
              password.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {sent ? (
            <p role="status" className="text-foreground text-sm leading-6">
              If an account exists for that email, we&apos;ve sent a reset link.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-foreground t-eyebrow">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={requestMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                {requestMutation.isPending ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          )}
          <p className="text-center text-sm">
            <Link to="/login" className="text-accent-text hover:underline">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function forgotErrorMessage(err: unknown): string {
  const status = apiErrorStatus(err);
  if (status === 403 && apiErrorDetail(err) === "sso_required") {
    return "BOI accounts sign in with Google.";
  }
  if (status === 429) {
    const wait = retryAfterLabel(err);
    return wait
      ? `Too many requests. Try again in ${wait}.`
      : "Too many requests. Try again later.";
  }
  return "Something went wrong. Please try again.";
}
